import { ClipboardCheck, Library } from "lucide-react";
import { useSignedInRole, useSignedInRoles } from "../shell/signedInUser";
import { evalPersonaForRoles, type EvalPersona } from "./evalPersona";

// The one visible difference between the two personas on the shared screen.
//
// Both work the SAME surface — that is a settled product decision, and there is
// deliberately no mode toggle and no second route. What differs is what the
// list in front of them MEANS: an auditor is looking at every captured case, a
// reviewer is looking at an assignment. The rows are identical either way, so
// without this the two readings are indistinguishable, and the reviewer's is
// the dangerous one to get wrong — a queue-scoped list mistaken for the whole
// corpus makes unassigned work look like work that does not exist.
//
// This states what the server already decided (see evalPersona.ts). It gates
// nothing, hides nothing, and unlocks nothing.

export function EvalScopeBanner({
  batchCount,
  /** Injectable for tests; defaults to the session's persona. */
  persona: personaOverride,
}: {
  batchCount: number;
  persona?: EvalPersona;
}) {
  const roles = useSignedInRoles();
  const legacyRole = useSignedInRole();

  // The full set when the session reported one; the single legacy field
  // otherwise. The fallback is not decoration — a client running ahead of a
  // server that has not shipped `roles` yet would otherwise resolve "unknown"
  // and render NOTHING, and the sentence that disappears is the reviewer's
  // "batches outside your queue are not shown", which is the one whose absence
  // actually misleads. Falling back reproduces the old understating behaviour,
  // which is the safe direction.
  const persona =
    personaOverride ??
    evalPersonaForRoles(roles ?? (legacyRole ? [legacyRole] : null));

  // No principal, or a role the server's rule does not recognise. Claim
  // NOTHING — a wrong scope label is worse than an absent one, and there is no
  // safe default between "your queue" and "everything".
  if (persona === "unknown") return null;

  const isAuditor = persona === "all-batches";
  const Icon = isAuditor ? Library : ClipboardCheck;

  return (
    <div
      data-testid="eval-scope-banner"
      data-persona={persona}
      className={`flex items-start gap-2.5 rounded-coronis border px-3.5 py-2.5 ${
        isAuditor
          ? "border-coronis-blue-200 bg-coronis-blue-50/60"
          : "border-border bg-muted/40"
      }`}
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[13px] font-semibold text-foreground">
          {isAuditor ? "All captured cases" : "Your review queue"}
        </span>
        <span className="text-[11.5px] leading-relaxed text-muted-foreground">
          {isAuditor ? (
            <>
              Oversight view — every batch captured for the study, not an
              assignment. Read-only: verdicts are recorded from assigned
              reviewers only.
            </>
          ) : (
            <>
              {batchCount} {batchCount === 1 ? "batch is" : "batches are"}{" "}
              assigned to you. Batches outside your queue are not shown here.
            </>
          )}
        </span>
      </div>
    </div>
  );
}
